import {API_BASE_URL} from "../config/constants";
import {AllTimeLeaderboardAllResponse, WeeklyLeaderboardAllResponse, SharedScoreResponse} from "../types/leaderboard";
import {UserProfile, ProfileRankingsResponse} from "../types/profile";
import {clearAllCaches} from "../state/leaderboardCache";

async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  
  // Get token from localStorage
  const token = localStorage.getItem('turnstile_token');
  
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(init?.headers || {})
  };

  // Automatically inject the Authorization header if the token exists
  // and if an Authorization header wasn't already explicitly provided
  // (though the merging logic here would mix them if they were separate keys, 
  // but if 'Authorization' is in init.headers, it overrides this logic if we aren't careful.
  // We'll check if Authorization is already set.)
  
  // Note: headers can be Headers object, array, or plain object.
  // For simplicity assuming it's a plain object or undefined here as per usage in this file.
  const hasAuth = init?.headers && 'Authorization' in init.headers;
  
  if (token && !hasAuth) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {...init, headers});

  if (!res.ok) {
    if (res.status === 401) {
      localStorage.removeItem('turnstile_token');
      clearAllCaches();
      window.dispatchEvent(new Event('auth-invalid'));
      // Return a pending promise to halt execution while VerificationGate re-renders
      return new Promise<T>(() => {});
    }

    const text = await res.text().catch(() => "");
    throw new Error(`Request to ${url} failed with ${res.status} . Body: ${text || "<empty>"}`);
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return (await res.json()) as T;
  }

  // If the worker ever returns non-JSON here, this keeps us from blowing up.
  return JSON.parse(await res.text()) as T;
}

export async function verifyTurnstileToken(token: string): Promise<string> {
  const res = await fetch(`${API_BASE_URL}/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ token })
  });

  if (!res.ok) {
    throw new Error('Verification failed');
  }

  const data = await res.json() as { token: string };
  return data.token;
}

export function fetchWeeklyLeaderboardAll(): Promise<WeeklyLeaderboardAllResponse> {
  return getJson<WeeklyLeaderboardAllResponse>("/leaderboard/weekly-all");
}

export function fetchAllTimeLeaderboardAll(): Promise<AllTimeLeaderboardAllResponse> {
  return getJson<AllTimeLeaderboardAllResponse>("/leaderboard/all-time-all");
}

export function fetchUserProfile(username: string): Promise<UserProfile> {
  return getJson<UserProfile>(`/users/profile?username=${encodeURIComponent(username)}`);
}

export function fetchUserActivity(username: string): Promise<Record<string, number>> {
  return getJson<Record<string, number>>(`/users/activity?username=${encodeURIComponent(username)}`);
}

export function fetchProfileRankings(userid: string): Promise<ProfileRankingsResponse> {
  return getJson<ProfileRankingsResponse>(`/users/rankings/${encodeURIComponent(userid)}`);
}

export function fetchSharedScore(gameId: string): Promise<SharedScoreResponse> {
  return getJson<SharedScoreResponse>(`/score/${encodeURIComponent(gameId)}`);
}
